import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';

interface StepConfig {
  labelKey: string;
  durationMs: number;
}

interface StepState extends StepConfig {
  startedAt?: number;
  completedAt?: number;
}

const STEP_CONFIGS: StepConfig[] = [
  { labelKey: 'smart_inbox.compose.ai_loader.step_analyzing', durationMs: 2000 },
  { labelKey: 'smart_inbox.compose.ai_loader.step_catalog_stock', durationMs: 3000 },
  { labelKey: 'smart_inbox.compose.ai_loader.step_documentation', durationMs: 3500 },
  { labelKey: 'smart_inbox.compose.ai_loader.step_pricing', durationMs: 0 },
];

@Component({
  selector: 'app-ai-generation-loader',
  standalone: true,
  templateUrl: './ai-generation-loader.component.html',
  styleUrls: ['./ai-generation-loader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, TranslateModule, UiModule],
})
export class AiGenerationLoaderComponent implements OnInit, OnDestroy {
  steps: StepState[] = STEP_CONFIGS.map(s => ({ ...s }));
  currentStepIndex = 0;
  expanded = true;

  private stepTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.steps[0].startedAt = Date.now();
    this.scheduleAdvance();
  }

  ngOnDestroy(): void {
    if (this.stepTimer) {
      clearTimeout(this.stepTimer);
      this.stepTimer = null;
    }
  }

  get currentStep(): StepState {
    return this.steps[this.currentStepIndex];
  }

  /** Progress bar: reflects completed steps (not the current one). */
  get progressPercent(): number {
    return Math.round((this.currentStepIndex / this.steps.length) * 100);
  }

  get visibleSteps(): StepState[] {
    return this.steps.slice(0, this.currentStepIndex + 1);
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.cdr.markForCheck();
  }

  private scheduleAdvance(): void {
    const duration = this.steps[this.currentStepIndex].durationMs;
    if (!duration) return;

    this.stepTimer = setTimeout(() => {
      const now = Date.now();
      this.steps[this.currentStepIndex].completedAt = now;
      this.currentStepIndex++;
      this.steps[this.currentStepIndex].startedAt = now;
      this.cdr.markForCheck();
      this.scheduleAdvance();
    }, duration);
  }
}
